import * as React from 'react';
import { compose, setDisplayName, withHandlers, withState, withProps, lifecycle } from 'recompose';
import { TranslateProps } from 'util/i18n';
import { ReportSettings, ReportSettingsChangeHandler } from 'logic/reports/ReportSettings';
import { makeSentenceFragments, FragmentRenderer, renderFragment } from 'util/makeSentenceFragments';
import { PositionPicker } from './PositionPicker';
import { DayPicker } from './DayPicker';
import { Input } from 'ui/shared/Input';

export type OwnProps = TranslateProps & {
    enabled: boolean;
    useDayNum: boolean;
    onChangeUseDayNum: (b: boolean) => void;
    reportSettings: Readonly<ReportSettings>;
    onChange: ReportSettingsChangeHandler;
};

const RawRecurrenceMonthlyDay = ({ useDayNum, handleChangeUseDay, enabled, sentenceFragments, renderFragment }: OwnProps & RecomposeProps) =>
    <div>
        <label>
            <input
                type="radio"
                id="edit-report-monthly-use-day"
                checked={ !useDayNum }
                onChange={ handleChangeUseDay }
                disabled={ !enabled }
            />
        { sentenceFragments.map(renderFragment) }
        </label>
    </div>
;

type RecomposeProps = {
    sentenceFragments: string[];
    renderFragment: FragmentRenderer;
    day: string;
    position: string;
    frequency: string;
    handleChangeUseDay: React.ChangeEventHandler<HTMLInputElement>;
    handleChangeFrequency: React.ChangeEventHandler<HTMLInputElement>;
    handleChangePosition: React.ChangeEventHandler<HTMLSelectElement>;
    handleChangeDay: React.ChangeEventHandler<HTMLSelectElement>;
};
type _RecomposeProps = RecomposeProps & {
    _setDay: (f: string) => void;
    _setPosition: (f: string) => void;
    _setFrequency: (f: string) => void;
};

const update = ({ onChange, useDayNum, day, position, frequency }: OwnProps & _RecomposeProps) => {
    if (!useDayNum) {
        onChange(reportSettings =>
            reportSettings
                .setScheduleMonthlyPosition(position)
                .setScheduleMonthlyDay(day)
                .setScheduleFrequency(parseInt(frequency, 10))
        );
    }
};

export const RecurrenceMonthlyDay = compose<OwnProps & RecomposeProps, OwnProps>(
    setDisplayName('RecurrenceMonthlyDay'),
    withState('position', '_setPosition', ({ reportSettings }: OwnProps) => reportSettings.scheduleMonthlyPosition),
    withState('day', '_setDay', ({ reportSettings }: OwnProps) => reportSettings.scheduleMonthlyDay),
    withState('frequency', '_setFrequency', ({ reportSettings }: OwnProps) =>
        reportSettings.scheduleType === 'Monthly'
            ? reportSettings.useScheduleMonthlyDayNum
                ? '1'
                : reportSettings.scheduleFrequency.toString()
            : '1',
    ),
    withHandlers({
        handleChangeUseDay: (props: OwnProps & _RecomposeProps) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const useDayNum = !e.target.checked;
            props.onChangeUseDayNum(useDayNum);
            update({ ...props, useDayNum });
        },
        handleChangePosition: (props: OwnProps & _RecomposeProps) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const useDayNum = false;
            const position = e.target.value;
            props._setPosition(position);
            props.onChangeUseDayNum(useDayNum);
            update({ ...props, position, useDayNum });
        },
        handleChangeDay: (props: OwnProps & _RecomposeProps) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const useDayNum = false;
            const day = e.target.value;
            props._setDay(day);
            props.onChangeUseDayNum(useDayNum);
            update({ ...props, day, useDayNum });
        },
        handleChangeFrequency: (props: OwnProps & _RecomposeProps) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const useDayNum = false;
            const frequency = e.target.value;
            props._setFrequency(frequency);
            props.onChangeUseDayNum(useDayNum);
            update({ ...props, frequency, useDayNum });
        },
    }),
    lifecycle({
        componentWillUpdate(newProps: OwnProps & _RecomposeProps) {
            // If this component becomes enabled, need to apply the current values
            // to make sure the XML reflects the values in the UI.
            if (!this.props.enabled && newProps.enabled) {
                update(newProps);
            }
        },
    }),
    withProps(({ frequency, t, enabled, ...props }: OwnProps & RecomposeProps) => ({
        sentenceFragments: makeSentenceFragments(t('WC_REPORT_SCHEDULE_MONTHLY_DAY_SENTENCE', { count: parseInt(frequency, 10) })),
        renderFragment: renderFragment({
            NTH: (
                <PositionPicker
                    id="edit-report-monthly-position"
                    value={ props.position }
                    onChange={ props.handleChangePosition }
                    disabled={ !enabled }
                />
            ),
            DAY: (
                <DayPicker
                    id="edit-report-monthly-day"
                    value={ props.day }
                    onChange={ props.handleChangeDay }
                    disabled={ !enabled }
                />
            ),
            INTERVAL: (
                <Input
                    type="number"
                    id="edit-report-monthly-day-interval"
                    value={ frequency }
                    onChange={ props.handleChangeFrequency }
                    min={ 1 }
                    max={ 12 }
                    maxLength={ 2 }
                    disabled={ !enabled }
                />
            ),
        }),
    })),
)(RawRecurrenceMonthlyDay);



// WEBPACK FOOTER //
// ./src/ui/pages/designer/sections/schedule/runOnSchedule/recurrence/RecurrenceMonthlyDay.tsx